/**
 * Manual PO attribution — telling the agent which ServiceTitan purchase order
 * an invoice or receipt belongs to, when it reconciled against the wrong one,
 * proposed a new PO for a document that already has one, or never found it.
 * A button owning the dialog and the mutation, like `manual-job-match.tsx`.
 *
 * The PO is authoritative: one on another job moves the document there. The
 * server marks the choice manual, the agent keeps to it, and a change rejects
 * the agent's open proposals and queues a fresh run.
 */
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  CommandGroup,
  CommandInput,
  CommandList,
} from "@volca/ui/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import {
  useMatchDocumentPurchaseOrder,
  useSearchDocumentPurchaseOrders,
} from "@/accounts-payable/query";
import type {
  ApDocumentType,
  ApPurchaseOrderOption,
} from "@/accounts-payable/types";
import {
  documentTypeLabel,
  purchaseOrderLabel,
  purchaseOrderSearchStatusMessage,
} from "@/accounts-payable/utils";
import { getErrorMessage } from "@/core/api/errors";
import { useDebounce } from "@/core/hooks";
import { PickerCommand } from "./picker-command";
import { PurchaseOrderOptionItem } from "./purchase-order-option";

/** Each distinct query is a live ServiceTitan lookup, so wait for a pause. */
const SEARCH_DEBOUNCE_MS = 400;

/**
 * The trigger's look: a button (sized and coloured like any other), or a link
 * beside a review row's details, which takes neither.
 */
type PurchaseOrderMatchTrigger =
  | {
      appearance?: "button";
      size?: "xs" | "sm";
      variant?: "default" | "secondary" | "outline" | "ghost";
    }
  | { appearance: "link"; size?: never; variant?: never };

export const PurchaseOrderMatchButton = ({
  businessId,
  documentId,
  documentType,
  agentRunId,
  hasJob,
  label = "Match to a PO",
  ariaLabel,
  className,
  ...trigger
}: {
  businessId: string | null;
  documentId: string;
  documentType: ApDocumentType;
  /** The document's newest run id, to tell whether the pick queued a new one. */
  agentRunId: string | null;
  /** Whether the document has a job, whose POs the dialog opens on. */
  hasJob: boolean;
  label?: string;
  /** For a list that repeats the label on every row: which one this is. */
  ariaLabel?: string;
  className?: string;
} & PurchaseOrderMatchTrigger) => {
  const [open, setOpen] = useState(false);
  // Here rather than in the form: the form unmounts with the dialog, and
  // React Query only runs mutate-level callbacks while the observer lives.
  const matchPo = useMatchDocumentPurchaseOrder(businessId, documentType);
  const kind = documentTypeLabel(documentType);

  return (
    <>
      {trigger.appearance === "link" ? (
        <Button
          variant="link"
          className={cn("h-auto p-0", className)}
          aria-label={ariaLabel}
          onClick={() => setOpen(true)}
        >
          {label}
        </Button>
      ) : (
        <Button
          size={trigger.size ?? "default"}
          variant={trigger.variant ?? "secondary"}
          className={className}
          aria-label={ariaLabel}
          onClick={() => setOpen(true)}
        >
          {label}
        </Button>
      )}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          // A save in flight holds the dialog open, so its outcome has
          // somewhere to land.
          if (matchPo.isPending) return;
          setOpen(next);
        }}
      >
        <DialogContent className="gap-6 sm:max-w-[560px]">
          <DialogHeader className="pr-8">
            <DialogTitle>Match {kind.lower} to a purchase order</DialogTitle>
            <DialogDescription>
              The agent will reconcile this {kind.lower} against the PO you
              choose and won’t file another. A PO on a different job moves the{" "}
              {kind.lower} to that job.
            </DialogDescription>
          </DialogHeader>
          {open && (
            <PurchaseOrderMatchForm
              businessId={businessId}
              documentId={documentId}
              documentType={documentType}
              agentRunId={agentRunId}
              hasJob={hasJob}
              matchPo={matchPo}
              onClose={() => setOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const PurchaseOrderMatchForm = ({
  businessId,
  documentId,
  documentType,
  agentRunId,
  hasJob,
  matchPo,
  onClose,
}: {
  businessId: string | null;
  documentId: string;
  documentType: ApDocumentType;
  agentRunId: string | null;
  hasJob: boolean;
  matchPo: ReturnType<typeof useMatchDocumentPurchaseOrder>;
  onClose: () => void;
}) => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<ApPurchaseOrderOption | null>(null);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const {
    data: options = [],
    isLoading,
    isError,
    isPlaceholderData,
  } = useSearchDocumentPurchaseOrders(
    businessId,
    documentType,
    documentId,
    debouncedQuery,
  );
  const kind = documentTypeLabel(documentType).lower;

  // A selection belongs to the results it was picked from (see the job match).
  const onQueryChange = (value: string) => {
    setQuery(value);
    if (value.trim() !== query.trim()) setSelected(null);
  };

  const statusMessage = purchaseOrderSearchStatusMessage({
    query,
    hasJob,
    isLoading: isLoading || isPlaceholderData || query !== debouncedQuery,
    isError,
    resultCount: options.length,
  });

  const save = (option: ApPurchaseOrderOption | null) =>
    matchPo.mutate(
      { documentId, stPurchaseOrderId: option?.st_purchase_order_id ?? null },
      {
        onSuccess: (document) => {
          const outcome = option
            ? `${documentTypeLabel(documentType).capitalized} matched to ${purchaseOrderLabel(option)}.`
            : `${documentTypeLabel(documentType).capitalized} marked as having no existing PO.`;
          // The server rejects the agent's open proposals and queues a run
          // that reads the pick, after a short wait so several edits share it.
          const rerunQueued =
            document.agent_run_id !== null &&
            document.agent_run_id !== agentRunId;
          // "Against it" only reads right when there is a PO to check against.
          const followUp = option
            ? `The agent will re-check this ${kind} against it in a couple of minutes.`
            : `The agent will re-check this ${kind} in a couple of minutes.`;
          toast.success(rerunQueued ? `${outcome} ${followUp}` : outcome);
          onClose();
        },
        onError: (error: unknown) =>
          toast.error("Couldn’t change the purchase order", {
            description: getErrorMessage(error),
          }),
      },
    );

  return (
    <>
      {/* min-w-0: the dialog is a grid, whose column otherwise grows to the
          longest unwrapped memo and pushes the footer's buttons off screen. */}
      <PickerCommand label="Search purchase orders" className="min-w-0">
        <CommandInput
          value={query}
          onValueChange={onQueryChange}
          disabled={matchPo.isPending}
          // The dialog opens on the job's POs; typing is the other way in.
          autoFocus
          placeholder="PO number or job number"
        />
        <CommandList>
          {!statusMessage && (
            <CommandGroup
              heading={query.trim() || !hasJob ? undefined : "POs on this job"}
            >
              {options.map((option) => (
                <PurchaseOrderOptionItem
                  key={option.st_purchase_order_id}
                  option={option}
                  selected={
                    selected?.st_purchase_order_id ===
                    option.st_purchase_order_id
                  }
                  disabled={matchPo.isPending}
                  onSelect={() => setSelected(option)}
                />
              ))}
            </CommandGroup>
          )}
        </CommandList>
        {statusMessage && (
          <Stack
            gap={4}
            align="center"
            role="status"
            className="w-full px-4 py-6 text-center text-sm text-subtle"
          >
            {statusMessage}
          </Stack>
        )}
      </PickerCommand>
      {/* One row at every width, like the pricebook dialog's footer. */}
      <DialogFooter className="flex-row flex-wrap items-center justify-between gap-3 sm:justify-between sm:gap-3">
        {/* For a document whose cost isn't filed anywhere yet: the agent then
            files a new PO rather than reconciling against one it found. */}
        {/* -ml-2.5 takes back the ghost padding, so its text, not its hover
            fill, lines up with the title above. */}
        <Button
          variant="ghost"
          className="-ml-2.5"
          onClick={() => save(null)}
          disabled={matchPo.isPending}
        >
          No existing PO
        </Button>
        <Stack align="start" direction="row" gap={2}>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={matchPo.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => selected && save(selected)}
            disabled={!selected || matchPo.isPending}
            aria-busy={matchPo.isPending || undefined}
          >
            {matchPo.isPending && (
              <Spinner aria-hidden data-icon="inline-start" />
            )}
            Use this PO
          </Button>
        </Stack>
      </DialogFooter>
    </>
  );
};
